"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, HINT } from "@/components/tool-kit/styles";
import {
  FitResults,
  IndependenceResults,
} from "@/components/tools/stats-tests/chi-square-results";
import {
  CountGrid,
  parseCounts,
  replaceAt,
  replaceCell,
} from "@/components/tools/stats-tests/count-grid";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import {
  chiSquareGoodnessOfFit,
  chiSquareIndependence,
  type GoodnessOfFitOutcome,
} from "@/lib/tool-kit/stats-tests/chi-square";

/** English strings. A translated content file overrides these through `ui`. */
export const CHI_SQUARE_CALCULATOR_STRINGS = {
  testLabel: "Test",
  independence: "Test of independence (contingency table)",
  goodness: "Goodness of fit",
  tableHint:
    "Type the observed counts. Row and column names are editable — they label the results.",
  cell: "Count for {row}, {column}",
  rowName: "Name of row {n}",
  columnName: "Name of column {n}",
  total: "Total",
  rowDefault: "Row {n}",
  columnDefault: "Column {n}",
  addRow: "Add row",
  removeRow: "Remove row",
  addColumn: "Add column",
  removeColumn: "Remove column",
  yates: "Apply Yates's continuity correction (2 × 2 tables only)",
  observedLabel:
    "Observed counts — whole numbers separated by commas, spaces, or new lines",
  observedPlaceholder: "30, 14, 34, 22",
  expectedLabel:
    "Expected proportions, percentages, or counts — optional; blank means equal",
  expectedPlaceholder: "0.25, 0.25, 0.25, 0.25",
  namesLabel: "Category names — optional, one per line or separated by commas",
  namesPlaceholder: "Prompt A, Prompt B, Prompt C, Prompt D",
  categoryDefault: "Category {n}",
  countsDetected: "one: count|other: counts",
  alphaLabel: "Significance level (α)",
  tryExample: "Try an example",
  exampleRows: "Online|In person|Hybrid",
  exampleColumns: "Passed|Failed",
  exampleCounts: "45, 15|30, 30|25, 15",
  exampleObserved: "30, 14, 34, 22",
  exampleNames: "Prompt A, Prompt B, Prompt C, Prompt D",
  results: "Results",
  chiSquare: "χ² (Pearson)",
  chiSquareYates: "χ² (Yates-corrected)",
  df: "Degrees of freedom",
  pValue: "p-value",
  n: "Total (N)",
  cramersV: "Cramér's V",
  phi: "φ (phi)",
  cohensW: "Cohen's w",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the two variables are associated.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence of an association.",
  decisionRejectFit:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the counts do not fit the expected proportions.",
  decisionKeepFit:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — the counts are consistent with the expected proportions.",
  lowExpected:
    "one: {count} expected count is below 5|other: {count} expected counts are below 5",
  lowExpectedDetail:
    "(the smallest is {min}), so the chi-square approximation may be unreliable.",
  fisherPointer: "For a 2 × 2 table, use Fisher's exact test instead:",
  fisherLink: "Fisher's exact test calculator",
  combinePointer:
    "Combine sparse categories where that makes sense, or use an exact test in statistical software.",
  expectedHeading: "Expected counts",
  expectedHint: "E = row total × column total ÷ N",
  residualsHeading: "Adjusted standardized residuals",
  residualsHint:
    "Cells beyond ±1.96 depart from independence at roughly the 5% level — a guide to where the association lies, not a formal post hoc test.",
  categoryColumn: "Category",
  observedColumn: "Observed",
  proportionColumn: "Expected proportion",
  expectedColumn: "Expected",
  contributionColumn: "(O − E)² / E",
  rescaledNote:
    "Expected values are rescaled to sum to N = {n}; only their ratios matter.",
  howCalculated: "How this was calculated",
  apaHeading: "APA-style result",
  apaTemplate: "χ²({df}, *N* = {n}) = {chi}, *p* {p}, *V* = {v}",
  apaTemplate2x2: "χ²({df}, *N* = {n}) = {chi}, *p* {p}, φ = {v}",
  apaTemplateFit: "χ²({df}, *N* = {n}) = {chi}, *p* {p}, *w* = {v}",
  apaNoteYates:
    "Reported with Yates's correction; the effect size comes from the uncorrected χ².",
  copyApa: "Copy APA result",
  copied: "Copied!",
  promptTable: "Fill every cell with a whole-number count — zero is allowed.",
  promptFit: "Enter at least two observed counts.",
  errorMargin:
    "A row or column adds up to zero, so its expected counts would be zero. Remove it or fill in its counts.",
  errorMismatch:
    "The lists do not match: {observed} observed counts but {expected} expected values.",
  errorExpected: "Every expected value must be greater than zero.",
  errorNotWhole: "Observed counts must be whole numbers of zero or more.",
} as const;

type Strings = typeof CHI_SQUARE_CALCULATOR_STRINGS;
type Mode = "independence" | "goodness";

const MIN_SIZE = 2;
const MAX_SIZE = 8;
const LIST_SPLIT = /\r?\n|,/;
const ROW_SPLIT = "|";

function labels(template: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => t(template, { n: i + 1 }));
}

function fitOutcome(
  observedText: string,
  expectedText: string
): GoodnessOfFitOutcome | "not-whole" | null {
  const observed = parseNumberList(observedText);
  if (observed.length < 2) {
    return null;
  }
  if (observed.some((value) => !Number.isInteger(value) || value < 0)) {
    return "not-whole";
  }
  const expected = parseNumberList(expectedText);
  return chiSquareGoodnessOfFit(
    observed,
    expected.length > 0 ? expected : null
  );
}

function fitMessage(
  outcome: GoodnessOfFitOutcome | "not-whole" | null,
  strings: Strings,
  counts: { observed: string; expected: string }
): string {
  if (outcome === "not-whole") {
    return strings.errorNotWhole;
  }
  if (outcome === null || outcome.ok) {
    return strings.promptFit;
  }
  if (outcome.reason === "length-mismatch") {
    return t(strings.errorMismatch, counts);
  }
  if (outcome.reason === "bad-expected") {
    return strings.errorExpected;
  }
  return strings.promptFit;
}

function GridControls({
  rows,
  cols,
  onRows,
  onCols,
  strings,
}: {
  rows: number;
  cols: number;
  onRows: (delta: number) => void;
  onCols: (delta: number) => void;
  strings: Strings;
}) {
  const buttons = [
    { label: strings.addRow, disabled: rows >= MAX_SIZE, run: () => onRows(1) },
    {
      label: strings.removeRow,
      disabled: rows <= MIN_SIZE,
      run: () => onRows(-1),
    },
    {
      label: strings.addColumn,
      disabled: cols >= MAX_SIZE,
      run: () => onCols(1),
    },
    {
      label: strings.removeColumn,
      disabled: cols <= MIN_SIZE,
      run: () => onCols(-1),
    },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {buttons.map((button) => (
        <button
          className={BUTTON_SMALL}
          disabled={button.disabled}
          key={button.label}
          onClick={button.run}
          type="button"
        >
          {button.label}
        </button>
      ))}
    </div>
  );
}

export default function ChiSquareCalculatorWidget() {
  const strings = useToolUi(CHI_SQUARE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("independence");
  const [rowLabels, setRowLabels] = useState(() =>
    labels(strings.rowDefault, 2)
  );
  const [colLabels, setColLabels] = useState(() =>
    labels(strings.columnDefault, 2)
  );
  const [cells, setCells] = useState<string[][]>([
    ["", ""],
    ["", ""],
  ]);
  const [yates, setYates] = useState(false);
  const [observed, setObserved] = useState("");
  const [expected, setExpected] = useState("");
  const [names, setNames] = useState("");
  const [alpha, setAlpha] = useState("0.05");

  function resizeRows(delta: number) {
    const next = cells.length + delta;
    if (next < MIN_SIZE || next > MAX_SIZE) {
      return;
    }
    setCells(
      delta > 0 ? [...cells, colLabels.map(() => "")] : cells.slice(0, next)
    );
    setRowLabels(
      delta > 0
        ? [...rowLabels, t(strings.rowDefault, { n: next })]
        : rowLabels.slice(0, next)
    );
  }

  function resizeCols(delta: number) {
    const next = colLabels.length + delta;
    if (next < MIN_SIZE || next > MAX_SIZE) {
      return;
    }
    setCells(
      cells.map((row) => (delta > 0 ? [...row, ""] : row.slice(0, next)))
    );
    setColLabels(
      delta > 0
        ? [...colLabels, t(strings.columnDefault, { n: next })]
        : colLabels.slice(0, next)
    );
  }

  function fillExample() {
    if (mode === "goodness") {
      setObserved(strings.exampleObserved);
      setExpected("");
      setNames(strings.exampleNames);
      return;
    }
    const rows = strings.exampleCounts
      .split(ROW_SPLIT)
      .map((row) => parseNumberList(row).map(String));
    setCells(rows);
    setRowLabels(strings.exampleRows.split(ROW_SPLIT));
    setColLabels(strings.exampleColumns.split(ROW_SPLIT));
  }

  const table = parseCounts(cells);
  const independence = table ? chiSquareIndependence(table) : null;
  const fit = fitOutcome(observed, expected);
  const categoryNames = names
    .split(LIST_SPLIT)
    .map((name) => name.trim())
    .filter((name) => name !== "");
  const observedCount = parseNumberList(observed).length;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.testLabel}
        name="chi-mode"
        onChange={setMode}
        options={[
          { value: "independence", label: strings.independence },
          { value: "goodness", label: strings.goodness },
        ]}
        value={mode}
      />
      {mode === "independence" ? (
        <div className="space-y-3">
          <p className={HINT}>{strings.tableHint}</p>
          <CountGrid
            colLabels={colLabels}
            fmt={fmt}
            idPrefix="chi"
            onCell={(i, j, value) => setCells(replaceCell(cells, i, j, value))}
            onColLabel={(j, value) =>
              setColLabels(replaceAt(colLabels, j, value))
            }
            onRowLabel={(i, value) =>
              setRowLabels(replaceAt(rowLabels, i, value))
            }
            rowLabels={rowLabels}
            text={strings}
            values={cells}
          />
          <GridControls
            cols={colLabels.length}
            onCols={resizeCols}
            onRows={resizeRows}
            rows={cells.length}
            strings={strings}
          />
          {cells.length === 2 && colLabels.length === 2 ? (
            <Checkbox
              checked={yates}
              label={strings.yates}
              onChange={setYates}
            />
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <TextAreaField
            hint={`${fmt(observedCount)}${tPlural(strings.countsDetected, locale, observedCount)}`}
            id="chi-observed"
            label={strings.observedLabel}
            onChange={setObserved}
            placeholder={strings.observedPlaceholder}
            rows={4}
            value={observed}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextAreaField
              id="chi-expected"
              label={strings.expectedLabel}
              onChange={setExpected}
              placeholder={strings.expectedPlaceholder}
              rows={3}
              value={expected}
            />
            <TextAreaField
              id="chi-names"
              label={strings.namesLabel}
              onChange={setNames}
              placeholder={strings.namesPlaceholder}
              prose
              rows={3}
              value={names}
            />
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="chi-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={alphaOptions(fmt)}
            value={alpha}
          />
        </div>
        <ExampleButton label={strings.tryExample} onClick={fillExample} />
      </div>
      {mode === "independence" && independence?.ok ? (
        <IndependenceResults
          alpha={Number(alpha)}
          colLabels={colLabels}
          fmt={fmt}
          locale={locale}
          r={independence.result}
          rowLabels={rowLabels}
          strings={strings}
          yates={yates}
        />
      ) : null}
      {mode === "independence" && !independence?.ok ? (
        <Notice>
          {independence &&
          !independence.ok &&
          independence.reason === "empty-margin"
            ? strings.errorMargin
            : strings.promptTable}
        </Notice>
      ) : null}
      {mode === "goodness" && fit !== null && fit !== "not-whole" && fit.ok ? (
        <FitResults
          alpha={Number(alpha)}
          customExpected={parseNumberList(expected).length > 0}
          fmt={fmt}
          locale={locale}
          names={categoryNames}
          r={fit.result}
          strings={strings}
        />
      ) : null}
      {mode === "goodness" &&
      !(fit !== null && fit !== "not-whole" && fit.ok) ? (
        <Notice>
          {fitMessage(fit, strings, {
            observed: fmt(observedCount),
            expected: fmt(parseNumberList(expected).length),
          })}
        </Notice>
      ) : null}
    </div>
  );
}
